import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getRefreshToken } from '../utils/auth';
import { decodeTokenClaims } from '../utils/tokenClaims';

const EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];
const MIN_IDLE_MS = 60 * 1000;
/** Browsers clamp/overflow setTimeout beyond 2^31-1 ms (~24.8d) — can fire immediately. */
const MAX_TIMEOUT_MS = 2147483647;

/**
 * Idle limit from refresh JWT `exp` only (backend-issued).
 * Clamped to MAX_TIMEOUT_MS so a 30d refresh token cannot overflow setTimeout.
 */
function getIdleMsFromRefreshToken() {
  const refresh = getRefreshToken();
  const claims = decodeTokenClaims(refresh);
  if (!claims?.exp) return null;
  const remainingMs = claims.exp * 1000 - Date.now();
  if (remainingMs <= 0) return MIN_IDLE_MS;
  return Math.min(MAX_TIMEOUT_MS, Math.max(MIN_IDLE_MS, remainingMs));
}

export default function IdleSessionGuard() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const idleMs = getIdleMsFromRefreshToken();
      if (idleMs == null) return;

      timerRef.current = setTimeout(async () => {
        await logout();
        navigate('/login', { replace: true });
      }, idleMs);
    };

    reset();
    EVENTS.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      EVENTS.forEach((ev) => window.removeEventListener(ev, reset));
    };
  }, [isAuthenticated, logout, navigate]);

  return null;
}
